import React, { useState } from 'react'
import { Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import * as FaIcons from 'react-icons/fa'
import SimpleMantenedorLayout from '../SimpleMantenedorLayout'
import { useMantenedores } from '../../context/MantenedoresContext'
import CierreTabs from './CierreTabs'
import ResumenFinanciero from './ResumenFinanciero'
import DetalleRutas from './DetalleRutas'
import AnalisisDetallado from './AnalisisDetallado'
import { useCierre } from '../../hooks/useCierre'

const CierreAcumulado = () => {
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()
    const permisos = userPermissions?.modules?.cierre || {}
    const { loadResumenDiarioDetallado, resumenDiario, loadingResumen, errorResumen, searchCombinationsFromErrors } = useCierre(user, permisos, { autoFetch: false })

    const [fechaInicio, setFechaInicio] = useState('')
    const [fechaFin, setFechaFin] = useState('')
    const [datosAcumulados, setDatosAcumulados] = useState(null)
    const [error, setError] = useState(null)
    const [validationError, setValidationError] = useState(null)

    // Función para validar el rango de fechas
    const validarRangoFechas = (inicio, fin) => {
        if (!inicio || !fin) {
            return 'Debe seleccionar fecha de inicio y fin'
        }

        const fechaInicio = new Date(inicio)
        const fechaFin = new Date(fin)
        const hoy = new Date()
        hoy.setHours(23, 59, 59, 999)

        // Validar que las fechas no sean futuras
        if (fechaInicio > hoy || fechaFin > hoy) {
            return 'Las fechas no pueden ser futuras'
        }

        // Validar que fecha inicio no sea mayor a fecha fin
        if (fechaInicio > fechaFin) {
            return 'La fecha de inicio debe ser anterior a la fecha de fin'
        }

        // Validar que el rango no exceda 40 días
        const diffTime = Math.abs(fechaFin - fechaInicio)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays > 40) {
            return 'El rango de fechas no puede exceder los 40 días'
        }

        return null
    }

    // Función para obtener los datos acumulados
    const obtenerDatosAcumulados = async () => {
        const validationErr = validarRangoFechas(fechaInicio, fechaFin)
        if (validationErr) {
            setValidationError(validationErr)
            return
        }

        setValidationError(null)
        setError(null)

        const resultado = await loadResumenDiarioDetallado(fechaInicio, fechaFin)

        if (resultado.success) {
            setDatosAcumulados(resultado.data)
        } else {
            setError(resultado.error)
        }
    }

    // Función para limpiar filtros
    const limpiarFiltros = () => {
        setFechaInicio('')
        setFechaFin('')
        setDatosAcumulados(null)
        setError(null)
        setValidationError(null)
    }

    // Funciones para formatear valores
    const formatearMonto = valor => {
        if (valor === null || valor === undefined || isNaN(valor)) return 'N/A'
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(valor)
    }

    const formatearPorcentaje = valor => {
        if (valor === null || valor === undefined || isNaN(valor)) return 'N/A'
        return `${Number(valor).toFixed(1)}%`
    }

    // Formatear fecha para mostrar (evita problemas de timezone)
    const formatearFechaDisplay = fecha => {
        if (!fecha) return 'N/A'

        // Si es string, convertir a Date
        const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha

        // Verificar si es una fecha válida
        if (isNaN(fechaObj.getTime())) return 'N/A'

        const year = fechaObj.getFullYear()
        const month = String(fechaObj.getMonth() + 1).padStart(2, '0')
        const day = String(fechaObj.getDate()).padStart(2, '0')
        return `${day}-${month}-${year}`
    }

    // Calcular días en el rango seleccionado
    const calcularDiasRango = () => {
        if (!fechaInicio || !fechaFin) return 0
        const inicio = new Date(fechaInicio)
        const fin = new Date(fechaFin)
        const diffTime = Math.abs(fin - inicio)
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 para incluir ambos días
    }

    // Configuración de tabs con datos acumulados
    const tabsConfig = datosAcumulados
        ? [
              {
                  eventKey: 'resumen',
                  title: 'Resumen Financiero',
                  component: (
                      <ResumenFinanciero
                          resumenDiario={datosAcumulados}
                          fechaConsulta={fechaInicio}
                          formatearMonto={formatearMonto}
                          formatearPorcentaje={formatearPorcentaje}
                          formatearFechaDisplay={formatearFechaDisplay}
                      />
                  )
              },
              {
                  eventKey: 'rutas',
                  title: 'Detalle de Rutas',
                  component: (
                      <DetalleRutas
                          resumenDiario={datosAcumulados}
                          formatearMonto={formatearMonto}
                          formatearPorcentaje={formatearPorcentaje}
                      />
                  )
              },
              {
                  eventKey: 'analisis',
                  title: 'Análisis Detallado',
                  component: (
                      <AnalisisDetallado
                          resumenDiario={datosAcumulados}
                          formatearMonto={formatearMonto}
                          formatearPorcentaje={formatearPorcentaje}
                          user={user}
                          permisos={permisos}
                          searchCombinationsFromErrors={searchCombinationsFromErrors}
                      />
                  )
              }
          ]
        : []

    return (
        <SimpleMantenedorLayout
            mantenedorId="cierre"
            title="Cierre Acumulado"
            subtitle="Análisis financiero por rango de fechas (máximo 40 días)"
            iconColor="#2e7d32"
            breadcrumbs={[{ label: 'Cierre', path: '/mantenedores/cierre' }, { label: 'Cierre Acumulado' }]}
            showBackButton={false}>
            {/* Selector de rango de fechas */}
            <Row className="mt-1">
                <Col xs={12}>
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-light border-0">
                            <h6 className="mb-0">
                                <FaIcons.FaCalendarCheck className="me-2" style={{ color: '#2e7d32' }} />
                                Selección de Rango de Fechas
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-dark">
                                            <FaIcons.FaCalendarAlt size={16} className="me-1" />
                                            Fecha Inicio
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fechaInicio}
                                            onChange={e => setFechaInicio(e.target.value)}
                                            max={new Date().toISOString().split('T')[0]}
                                            className="border-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-dark">
                                            <FaIcons.FaCalendarAlt size={16} className="me-1" />
                                            Fecha Fin
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fechaFin}
                                            onChange={e => setFechaFin(e.target.value)}
                                            max={new Date().toISOString().split('T')[0]}
                                            min={fechaInicio}
                                            className="border-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-dark">Días en Rango</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={calcularDiasRango()}
                                            readOnly
                                            className="bg-light border-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        onClick={obtenerDatosAcumulados}
                                        disabled={loadingResumen || !fechaInicio || !fechaFin}
                                        className="w-100">
                                        {loadingResumen ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    className="me-2"
                                                />
                                                Cargando...
                                            </>
                                        ) : (
                                            <>
                                                <FaIcons.FaChartLine className="me-2" />
                                                Obtener Resumen
                                            </>
                                        )}
                                    </Button>
                                    {/* <Button
                                        variant="outline-secondary"
                                        onClick={limpiarFiltros}
                                        disabled={loadingResumen}>
                                        Limpiar
                                    </Button> */}
                                </Col>
                            </Row>

                            {/* Alertas de error */}
                            {validationError && (
                                <Alert variant="warning" className="mb-3">
                                    <strong>Validación:</strong> {validationError}
                                </Alert>
                            )}

                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    <strong>Error:</strong> {error}
                                </Alert>
                            )}

                            {errorResumen && (
                                <Alert variant="danger" className="mb-3">
                                    <strong>Error del servidor:</strong> {errorResumen}
                                </Alert>
                            )}

                            {/* Información del rango seleccionado */}
                            {!datosAcumulados && fechaInicio && fechaFin && !validationError && (
                                <Alert variant="info" className="mb-3">
                                    <strong>Rango seleccionado:</strong>{' '}
                                    {new Date(fechaInicio).toLocaleDateString('es-CL')} al{' '}
                                    {new Date(fechaFin).toLocaleDateString('es-CL')} ({calcularDiasRango()} días)
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Tabs con datos */}
            {datosAcumulados && (
                <Row>
                    <Col>
                        <CierreTabs tabs={tabsConfig} defaultActiveKey="resumen" />
                    </Col>
                </Row>
            )}

            {/* Estado inicial sin datos */}
            {!datosAcumulados && !loadingResumen && !error && !validationError && (
                <Row>
                    <Col>
                        <Card className="border-0 shadow-sm text-center py-5">
                            <Card.Body>
                                <FaIcons.FaCalendarCheck size={64} className="text-muted mb-3" />
                                <h5 className="text-muted">Seleccione un rango de fechas</h5>
                                <p className="text-muted mb-0">
                                    Elija las fechas de inicio y fin para obtener el resumen acumulado.
                                    <br />
                                    El rango máximo permitido es de 40 días.
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </SimpleMantenedorLayout>
    )
}

export default CierreAcumulado
